import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, ThumbsUp, Filter, ChevronDown, Search, ArrowRight, Zap } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { IMG } from '../lib/images';
import { useTheme } from '../contexts/ThemeContext';

const SAMPLE_REVIEWS = [
  {
    id: 1, tool: 'ChatGPT Plus', slug: 'chatgpt-plus', category: 'Writing Assistants',
    author: 'Marcus T.', role: 'Content Director, SaaS startup', avatar: 'MT', date: 'Mar 2026',
    rating: 4.9, helpful: 142,
    headline: 'Transformed our content pipeline from 3 days to 4 hours',
    body: 'We were spending 3 days per week on content drafts alone. After integrating ChatGPT Plus with our CMS workflow, that dropped to 4 hours. The GPT-4o model handles nuanced briefs well, and the custom instructions feature means we stopped editing the same brand voice issues repeatedly. The only real limitation is context window on very long documents - we use Claude for those now.',
    pros: ['Fastest iteration on drafts', 'Excellent instruction-following', 'Code Interpreter for data tasks'],
    cons: ['Context limits for 10k+ word docs', 'Occasional hallucinations on citations'],
    verdict: 'Best all-round writing companion for content teams under 20 people.',
    score: { value: 4.9, ease: 4.8, support: 4.5, roi: 5.0 },
  },
  {
    id: 2, tool: 'Claude Pro', slug: 'claude-pro', category: 'Writing Assistants',
    author: 'Priya S.', role: 'Head of Marketing, B2B SaaS', avatar: 'PS', date: 'Mar 2026',
    rating: 4.8, helpful: 98,
    headline: 'The best AI for long-form research and nuanced copy',
    body: "Claude's 200k token context is a genuine differentiator. I feed it our full product docs and competitor analysis - it builds campaign copy with actual strategic awareness, not just surface-level text. The writing tone is more natural than GPT, which matters when you're writing for humans. Slightly slower on API but the quality justifies it.",
    pros: ['200k context handles entire projects', 'Most natural prose output', 'Excellent at tone matching'],
    cons: ['Slower response vs GPT-4o', 'No image generation'],
    verdict: 'Top pick for agencies and writers who care about voice and depth.',
    score: { value: 4.8, ease: 4.6, support: 4.7, roi: 4.9 },
  },
  {
    id: 3, tool: 'Midjourney v6', slug: 'midjourney-v6', category: 'Image Generation',
    author: 'Lena K.', role: 'Creative Lead, E-commerce', avatar: 'LK', date: 'Feb 2026',
    rating: 4.9, helpful: 217,
    headline: 'v6 is a step-change. Photo-realism that replaced our stock subscriptions.',
    body: 'We cancelled two stock photo subscriptions after v6. Product lifestyle shots, campaign visuals, social content - MJ handles all of it now. Describe -style reference and lighting and you get results that would have taken a junior designer half a day. The Discord workflow is clunky but manageable; the API access in enterprise tier is much cleaner.',
    pros: ['Photo-realistic outputs', 'Excellent brand style consistency', 'Fast iteration with -sref'],
    cons: ['Discord UI is dated', 'No native text overlay in images'],
    verdict: 'Unmatched image quality. Replace your stock subscription in week 1.',
    score: { value: 4.9, ease: 4.2, support: 4.0, roi: 5.0 },
  },
  {
    id: 4, tool: 'n8n', slug: 'n8n', category: 'Workflow Automation',
    author: 'James W.', role: 'Ops Lead, Digital Agency', avatar: 'JW', date: 'Mar 2026',
    rating: 4.7, helpful: 83,
    headline: 'Self-hosted automation that saved us $800/mo vs Zapier',
    body: 'We moved our entire automation stack from Zapier to n8n self-hosted. The AI agent nodes are genuinely impressive - we have an LLM-powered lead enrichment workflow running 24/7 that used to be a 2-person manual task. Fair-code license means we own our data. The learning curve is real (2-3 days to get fluent) but the payoff is worth it.',
    pros: ['Self-hosted = full data control', 'AI agent nodes built-in', 'Saves significant cost at scale'],
    cons: ['Steeper setup than Zapier', 'Self-host requires some DevOps'],
    verdict: 'Best automation platform for teams serious about owning their stack.',
    score: { value: 4.7, ease: 3.9, support: 4.5, roi: 5.0 },
  },
  {
    id: 5, tool: 'Jasper', slug: 'jasper', category: 'Writing Assistants',
    author: 'Sarah B.', role: 'SEO Content Manager', avatar: 'SB', date: 'Jan 2026',
    rating: 4.2, helpful: 61,
    headline: 'Solid for SEO content but the price-to-value gap is widening',
    body: "Jasper's brand voice training and SEO features are genuinely useful. The Surfer integration speeds up optimised article production. But at $99/month you're paying a premium that's getting harder to justify vs Claude + a cheaper SEO tool. The output quality is good, not great - you'll still spend 30 mins editing most pieces.",
    pros: ['Good brand voice training', 'Solid Surfer SEO integration', 'Templates speed up standard formats'],
    cons: ['Expensive for what you get', 'Output needs heavy editing', 'GPT-based, not differentiated'],
    verdict: 'Good for pure SEO content factories. Look at Claude + SurferSEO for better value.',
    score: { value: 4.2, ease: 4.6, support: 4.3, roi: 3.7 },
  },
  {
    id: 6, tool: 'Make', slug: 'make', category: 'Workflow Automation',
    author: 'Alex R.', role: 'No-Code Consultant', avatar: 'AR', date: 'Feb 2026',
    rating: 4.6, helpful: 75,
    headline: 'The visual automation tool that finally clicked for my clients',
    body: 'Make\'s visual canvas makes it possible to hand automation workflows to non-technical clients. The AI module with OpenAI and Anthropic connectors is good enough for 80% of use cases without custom code. We\'ve built AI-powered CRM enrichment, invoice processing, and support triage workflows. Pricing scales fairly with usage.',
    pros: ['Visual canvas is genuinely intuitive', 'Great AI provider integrations', 'Fair usage-based pricing'],
    cons: ['Can get complex for multi-branch flows', 'Error handling less robust than n8n'],
    verdict: 'Best choice for non-technical teams who want automation without the dev overhead.',
    score: { value: 4.6, ease: 4.8, support: 4.4, roi: 4.7 },
  },
];

const CATEGORIES = ['All', ...Array.from(new Set(SAMPLE_REVIEWS.map((r) => r.category)))];
const SORT_OPTS = [
  { value: 'helpful', label: 'Most Helpful' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'recent', label: 'Most Recent' },
];

const StarRow = ({ score, size = 14 }) => (
  <div className="flex gap-0.5">
    {[1,2,3,4,5].map((s) => (
      <Star key={s} size={size}
        style={{ color: s <= Math.round(score) ? '#FFE400' : 'rgba(255,255,255,0.15)',
                 fill: s <= Math.round(score) ? '#FFE400' : 'none' }}
      />
    ))}
  </div>
);

const ScoreBar = ({ label, value, color }) => (
  <div>
    <div className="flex justify-between text-xs mb-1" style={{ color: 'var(--text-muted)' }}>
      <span>{label}</span><span style={{ color }}>{value.toFixed(1)}</span>
    </div>
    <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
      <div className="h-1.5 rounded-full transition-all" style={{ width: `${(value / 5) * 100}%`, background: color }} />
    </div>
  </div>
);

const ReviewsPage = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [sort, setSort] = useState('helpful');
  const [expanded, setExpanded] = useState(null);
  const [minRating, setMinRating] = useState(0);

  const filtered = useMemo(() => {
    let list = [...SAMPLE_REVIEWS];
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) => r.tool.toLowerCase().includes(q) || r.headline.toLowerCase().includes(q) || r.body.toLowerCase().includes(q));
    }
    if (category !== 'All') list = list.filter((r) => r.category === category);
    if (minRating > 0) list = list.filter((r) => r.rating >= minRating);
    if (sort === 'helpful') list.sort((a, b) => b.helpful - a.helpful);
    else if (sort === 'rating') list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [search, category, sort, minRating]);

  const avgRating = (SAMPLE_REVIEWS.reduce((a, r) => a + r.rating, 0) / SAMPLE_REVIEWS.length).toFixed(1);

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-code" data-testid="reviews-page">
      <Helmet>
        <title>AI Tool Reviews | Wowrolla</title>
        <meta name="description" content="In-depth user reviews of AI tools - rated on value, ease of use, ROI and support. Real operators, real verdicts." />
      </Helmet>

      {/* Hero */}
      <section
        className="relative overflow-hidden resource-hero"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(rgba(255,249,236,0.68), rgba(247,252,255,0.82)), url(${IMG.hero.trophyAward})`
            : `linear-gradient(rgba(8,8,18,0.72), rgba(8,8,18,0.94)), url(${IMG.hero.trophyAward})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 prime-aurora-bg" />
        <div className="container mx-auto px-6 pb-10 relative z-10">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <p className="text-xs font-mono uppercase tracking-[0.2em] mb-3" style={{ color: 'var(--accent-primary)' }}>Community Reviews</p>
          <h1 className="text-4xl sm:text-5xl font-black text-white mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>AI Tool Reviews</h1>
          <p className="text-lg max-w-2xl mb-6" style={{ color: 'var(--text-secondary)' }}>
            Real-world verdicts from operators who've run the tools in production. Rated on ROI, ease of use, and honest tradeoffs.
          </p>
          {/* Stats */}
          <div className="flex flex-wrap gap-6 mb-8">
            {[
              { label: 'Reviews', value: SAMPLE_REVIEWS.length },
              { label: 'Avg Rating', value: `${avgRating} / 5` },
              { label: 'Categories', value: CATEGORIES.length - 1 },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-2xl font-black" style={{ color: 'var(--accent-primary)' }}>{s.value}</div>
                <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-10">

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-8 p-4 rounded-xl" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
          <div className="relative flex-1 min-w-[180px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
            <input
              type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reviews..."
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg"
              style={{ background: 'var(--bg-base)', border: '1px solid var(--border-strong)', color: 'var(--text-primary)' }}
            />
          </div>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg"
            style={{ background: 'var(--bg-base)', border: '1px solid var(--border-strong)', color: 'var(--text-primary)' }}
          >
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)}
            className="px-3 py-2 text-sm rounded-lg"
            style={{ background: 'var(--bg-base)', border: '1px solid var(--border-strong)', color: 'var(--text-primary)' }}
          >
            {SORT_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <div className="flex items-center gap-2">
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Min rating:</span>
            {[0,4,4.5].map((v) => (
              <button key={v} onClick={() => setMinRating(v)}
                className="px-2.5 py-1.5 text-xs rounded-lg transition-colors"
                style={{ background: minRating === v ? 'var(--accent-primary)' : 'var(--bg-base)', color: minRating === v ? '#000' : 'var(--text-secondary)', border: '1px solid var(--border-strong)' }}
              >
                {v === 0 ? 'All' : `${v}+`}
              </button>
            ))}
          </div>
          <div className="text-xs self-center" style={{ color: 'var(--text-muted)' }}>{filtered.length} review{filtered.length !== 1 ? 's' : ''}</div>
        </div>

        {/* Review Cards */}
        <div className="space-y-5">
          {filtered.map((review, idx) => (
            <motion.div key={review.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.06 }}
              className="rounded-2xl overflow-hidden" style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}
            >
              {/* Card header */}
              <div className="p-5 pb-3">
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <Link to={`/tools/${review.slug}`} className="font-bold text-lg hover:underline" style={{ color: 'var(--text-primary)' }}>{review.tool}</Link>
                      <span className="text-xs px-2 py-0.5 rounded-md" style={{ background: 'rgba(0,255,135,0.1)', color: '#00ff87' }}>{review.category}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <StarRow score={review.rating} />
                      <span className="font-bold text-sm" style={{ color: '#FFE400' }}>{review.rating}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold" style={{ background: 'rgba(0,255,135,0.15)', color: '#00ff87' }}>{review.avatar}</div>
                      <div>
                        <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{review.author}</div>
                        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>{review.role}</div>
                      </div>
                    </div>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{review.date}</span>
                  </div>
                </div>
                <h3 className="font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>"{review.headline}"</h3>
                <p className={`text-sm leading-relaxed ${expanded === review.id ? '' : 'line-clamp-3'}`} style={{ color: 'var(--text-secondary)' }}>{review.body}</p>
                <button onClick={() => setExpanded(expanded === review.id ? null : review.id)}
                  className="text-xs mt-2 flex items-center gap-1 hover:underline" style={{ color: 'var(--accent-primary)' }}
                >
                  {expanded === review.id ? 'Show less' : 'Read full review'}
                  <ChevronDown size={12} style={{ transform: expanded === review.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                </button>
              </div>

              {/* Expanded detail */}
              {expanded === review.id && (
                <div className="px-5 pb-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-3">
                    {/* Pros */}
                    <div>
                      <p className="text-xs font-semibold mb-2" style={{ color: '#00ff87' }}>Pros</p>
                      <ul className="space-y-1">
                        {review.pros.map((p) => (
                          <li key={p} className="flex items-start gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                            <span style={{ color: '#00ff87', marginTop: '1px' }}>✓</span>{p}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {/* Cons */}
                    <div>
                      <p className="text-xs font-semibold mb-2" style={{ color: '#f97316' }}>Cons</p>
                      <ul className="space-y-1">
                        {review.cons.map((c) => (
                          <li key={c} className="flex items-start gap-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                            <span style={{ color: '#f97316', marginTop: '1px' }}>✕</span>{c}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {/* Score breakdown */}
                    <div className="space-y-2">
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-muted)' }}>Score Breakdown</p>
                      <ScoreBar label="Overall Value" value={review.score.value} color="#FFE400" />
                      <ScoreBar label="Ease of Use" value={review.score.ease} color="#00ff87" />
                      <ScoreBar label="Support" value={review.score.support} color="#a78bfa" />
                      <ScoreBar label="ROI" value={review.score.roi} color="#f97316" />
                    </div>
                  </div>
                  <div className="mt-4 p-3 rounded-xl" style={{ background: 'rgba(255,232,0,0.06)', border: '1px solid rgba(255,232,0,0.2)' }}>
                    <span className="text-xs font-semibold" style={{ color: '#FFE400' }}>Verdict: </span>
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>{review.verdict}</span>
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <button className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg transition-colors" style={{ background: 'var(--bg-base)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)' }}>
                      <ThumbsUp size={12} /> Helpful ({review.helpful})
                    </button>
                    <Link to={`/tools/${review.slug}`} className="flex items-center gap-1 text-xs" style={{ color: 'var(--accent-primary)' }}>
                      View Full Tool Profile <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-14 p-6 rounded-2xl flex flex-col sm:flex-row items-center justify-between gap-5"
          style={{ background: 'linear-gradient(135deg, rgba(0,255,135,0.06), rgba(255,232,0,0.04))', border: '1px solid rgba(0,255,135,0.2)' }}
        >
          <div>
            <h3 className="font-bold text-lg mb-1 text-white">Not sure which tool fits your workflow?</h3>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Use our Find Tool quiz or compare two tools head-to-head.</p>
          </div>
          <div className="flex gap-3">
            <Link to="/find" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm" style={{ background: 'var(--accent-primary)', color: '#000' }}>
              <Zap size={14} /> Find My Tool
            </Link>
            <Link to="/compare" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm" style={{ border: '1px solid var(--border-strong)', color: 'var(--text-secondary)' }}>
              Compare
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ReviewsPage;


